/**
 * PURPOSE: Editor de una disciplina del Cómputo General (FTR-0066): puesto y participación por institución inscrita.
 * RESPONSIBILITIES: Tabla con logo, sigla y nombre; input "Puesto" (vacío o entero 1..N; repetidos y huecos
 *   permitidos; en rojo si no es válido); casilla "Participación (+2)"; puntos de la fila y total de la disciplina
 *   calculados en vivo con `scoring`; leyenda de puntos; Enter baja al puesto de la fila siguiente (y selecciona su
 *   texto); Guardar / Descartar cambios con su mensaje; última edición (quién y cuándo).
 * COLLABORATORS: ComputoGeneralArea.jsx (estado del borrador y guardado), computoGeneral.utils.js.
 * ROUTE: winscore/src/area/ComputoGeneralArea/ComputoDisciplineEditor.jsx
 */
import React, { useMemo, useRef, useState } from "react";
import { FaSave, FaUndo } from "react-icons/fa";

import { formatLastUpdate, formatScoringLegend, maxPlaceFor } from "./computoGeneral.utils.js";

const FEEDBACK_CLASSES = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  info: "border-slate-200 bg-slate-50 text-slate-700",
  error: "border-red-200 bg-red-50 text-red-700",
};

const InstitutionLogo = ({ institution }) =>
  institution.logo ? (
    <img src={institution.logo} alt="" className="h-8 w-8 shrink-0 rounded-full bg-white object-contain ring-1 ring-slate-200" />
  ) : (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-black text-slate-500 ring-1 ring-slate-200">
      {String(institution.abbreviation || institution.name || "?").slice(0, 3)}
    </span>
  );

const ComputoDisciplineEditor = ({
  data,
  rows,
  summary,
  dirty,
  saving,
  feedback,
  onPlaceChange,
  onParticipationChange,
  onSave,
  onDiscard,
}) => {
  const placeInputs = useRef([]);
  // Solo vista: por defecto el orden alfabético de siempre; activado, por puesto (sin puesto o inválido al final).
  const [sortByPlace, setSortByPlace] = useState(false);
  const discipline = data?.discipline || {};
  const enrolled = rows.length;
  const max = maxPlaceFor(enrolled, data?.scoring);
  const participationPoints = Number(data?.scoring?.participationPoints) || 0;
  const lastUpdate = formatLastUpdate(discipline.lastUpdate);
  const canSave = dirty && summary.invalid === 0 && !saving;
  const shownRows = useMemo(() => {
    if (!sortByPlace) return rows;
    const rank = (row) => (row.valid && row.place !== null ? row.place : Number.MAX_SAFE_INTEGER);
    return rows.map((row, position) => ({ row, position })).sort((a, b) => rank(a.row) - rank(b.row) || a.position - b.position).map((item) => item.row);
  }, [rows, sortByPlace]);

  const handlePlaceKeyDown = (event, index) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const next = placeInputs.current[index + 1];
    if (next) {
      next.focus();
      next.select();
    }
  };

  return (
    <div className="min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h3 className="text-xl font-bold text-gray-900">
            {discipline.label}
            {discipline.acronym ? <span className="ml-2 text-sm font-semibold text-gray-400">{discipline.acronym}</span> : null}
          </h3>
          <p className="text-sm font-medium text-gray-500">
            {enrolled} {enrolled === 1 ? "institución inscrita" : "instituciones inscritas"} · puesto de 1 a {max}, puede repetirse o quedar
            vacío.
          </p>
          <p className="mt-1 text-xs font-semibold text-gray-500">
            {lastUpdate ? `Última edición: ${lastUpdate}` : "Sin ediciones todavía."}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={onDiscard}
            disabled={!dirty || saving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaUndo />
            Descartar cambios
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaSave />
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
        Puntos: {formatScoringLegend(data?.scoring)}
      </p>

      {summary.invalid > 0 ? (
        <p role="alert" className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
          Revisa los puestos en rojo: deben ser números enteros entre 1 y {max} (o quedar vacíos).
        </p>
      ) : null}

      {feedback ? (
        <p role="status" className={`mt-3 rounded-xl border px-3 py-2 text-sm font-bold ${FEEDBACK_CLASSES[feedback.type] || FEEDBACK_CLASSES.info}`}>
          {feedback.message}
        </p>
      ) : null}

      <label className="mt-4 inline-flex cursor-pointer items-center gap-2 text-sm font-bold text-gray-700">
        <input
          type="checkbox"
          role="switch"
          aria-checked={sortByPlace}
          checked={sortByPlace}
          onChange={(event) => setSortByPlace(event.target.checked)}
          className="h-4 w-4 cursor-pointer accent-blue-600"
        />
        Ordenar por puesto
      </label>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full min-w-[480px] text-left">
          <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Institución</th>
              <th className="w-24 px-3 py-2 text-center">Puesto</th>
              <th className="w-32 px-3 py-2 text-center">Participación (+{participationPoints})</th>
              <th className="w-20 px-3 py-2 text-center">Puntos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shownRows.map((row, index) => {
              const { institution } = row;
              const label = institution.abbreviation || institution.name;
              return (
                <tr key={institution.id} className={row.changed ? "bg-amber-50/60" : ""}>
                  <td className="px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <InstitutionLogo institution={institution} />
                      <div className="min-w-0">
                        <div className="text-sm font-black text-gray-900">{institution.abbreviation || "—"}</div>
                        <div className="truncate text-xs font-medium text-gray-500" title={institution.name}>
                          {institution.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      ref={(element) => {
                        placeInputs.current[index] = element;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={4}
                      value={row.placeText}
                      placeholder="—"
                      onChange={(event) => onPlaceChange(institution.id, event.target.value)}
                      onKeyDown={(event) => handlePlaceKeyDown(event, index)}
                      onFocus={(event) => event.target.select()}
                      disabled={saving}
                      aria-label={`Puesto de ${label}`}
                      aria-invalid={row.valid ? "false" : "true"}
                      title={row.valid ? `Puesto de 1 a ${max}` : `Debe ser un número entero entre 1 y ${max}`}
                      className={[
                        "mx-auto w-16 rounded-lg border px-2 py-1.5 text-center text-base font-bold outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100",
                        row.valid
                          ? "border-gray-300 text-gray-900 focus:border-blue-400 focus:ring-blue-100"
                          : "border-red-500 bg-red-50 text-red-700 focus:border-red-500 focus:ring-red-100",
                      ].join(" ")}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-gray-700">
                      <input
                        type="checkbox"
                        checked={row.participation}
                        onChange={(event) => onParticipationChange(institution.id, event.target.checked)}
                        disabled={saving}
                        aria-label={`Participación de ${label}`}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span className={row.participation ? "text-emerald-700" : "text-gray-400"}>+{row.participationPoints}</span>
                    </label>
                  </td>
                  <td className="px-3 py-2 text-center text-base font-black text-gray-900" data-testid={`computo-points-${institution.id}`}>
                    {row.points}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-gray-200 bg-gray-50 text-sm font-black text-gray-800">
            <tr>
              <td className="px-3 py-2">Total de la disciplina</td>
              <td className="px-3 py-2 text-center text-xs font-bold text-gray-500">{summary.placePoints} por puesto</td>
              <td className="px-3 py-2 text-center text-xs font-bold text-gray-500">+{summary.participationPoints}</td>
              <td className="px-3 py-2 text-center text-base" data-testid="computo-discipline-total">
                {summary.points}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ComputoDisciplineEditor;
